// ─────────────────────────────────────────────────────────────
// poReport.config.js — Report 2: PO list report (all / vendor-wise)
// Shape: list (landscape)
// Data:  GET /api/tape/purchase-orders?vendor_id=&status=&from_date=&to_date=
// ─────────────────────────────────────────────────────────────

import { SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

const STATUS_LABEL = {
  pending: 'Pending',
  partial: 'Partial',
  received: 'Received',
  cancelled: 'Cancelled',
};

export const poReportConfig = (orders, filters) => {
  const list = orders || [];
  const f = filters || {};

  const rows = list.map((o) => {
    const gst = num(o.gst_amount);
    const total = num(o.total_amount);
    return {
      po_number: o.po_number,
      po_date: o.po_date,
      vendor_name: o.vendor_name,
      line_count: o.line_count,
      total_ordered: o.total_ordered,
      total_received: o.total_received,
      pending: num(o.total_ordered) - num(o.total_received),
      subtotal: total - gst,
      gst_amount: gst,
      total_amount: total,
      // cancelled orders are still listed but greyed in meaning —
      // tone keeps that decision here, not in the engine
      __tone: o.status === 'cancelled' ? 'expense' : null,
    };
  });

  const sum = (key) => rows.reduce((s, r) => s + num(r[key]), 0);

  const subtotal = sum('subtotal');
  const gstTotal = sum('gst_amount');
  const grand = sum('total_amount');
  const ordered = sum('total_ordered');
  const received = sum('total_received');
  const pending = sum('pending');

  return {
    kind: 'list',
    orientation: 'l',
    title: 'Purchase Order Report',
    subtitle: `${FORMATTERS.date(f.from_date)}  to  ${FORMATTERS.date(f.to_date)}`,

    boxes: [
      {
        heading: 'Report Filters',
        rows: [
          { label: 'Vendor', value: f.vendor_name || 'All Vendors' },
          { label: 'Status', value: f.status ? STATUS_LABEL[f.status] : 'All Status' },
          { label: 'From Date', value: FORMATTERS.date(f.from_date) },
          { label: 'To Date', value: FORMATTERS.date(f.to_date) },
        ],
      },
      {
        heading: 'Summary',
        rows: [
          {
            label: 'Orders',
            value: rows.length,
            label2: 'Rolls Ordered',
            value2: FORMATTERS.qty(ordered),
          },
          {
            label: 'Rolls Received',
            value: FORMATTERS.qty(received),
            label2: 'Rolls Pending',
            value2: FORMATTERS.qty(pending),
          },
          {
            label: 'Goods Value',
            value: FORMATTERS.money(subtotal),
            label2: 'GST',
            value2: FORMATTERS.money(gstTotal),
          },
          { label: 'Total Value', value: FORMATTERS.money(grand) },
        ],
      },
    ],

    columns: [
      { key: 'po_number', label: 'PO #', align: 'left', fmt: 'text', width: 22 },
      { key: 'po_date', label: 'Date', align: 'left', fmt: 'date', width: 24 },
      { key: 'vendor_name', label: 'Vendor', align: 'left', fmt: 'text' },
      { key: 'line_count', label: 'Lines', align: 'center', fmt: 'int', width: 14 },
      { key: 'total_ordered', label: 'Ordered', align: 'right', fmt: 'qty', width: 20 },
      { key: 'total_received', label: 'Received', align: 'right', fmt: 'qty', width: 20 },
      { key: 'pending', label: 'Pending', align: 'right', fmt: 'qty', width: 20 },
      { key: 'subtotal', label: 'Subtotal', align: 'right', fmt: 'money', width: 26 },
      { key: 'gst_amount', label: 'GST', align: 'right', fmt: 'money', width: 22 },
      { key: 'total_amount', label: 'Total', align: 'right', fmt: 'money', width: 28, bold: true },
    ],

    rows,

    columnTotals: [
      'total_ordered',
      'total_received',
      'pending',
      'subtotal',
      'gst_amount',
      'total_amount',
    ],
    totalLabel: 'TOTAL',

    totals: [
      { label: 'Goods Subtotal', value: subtotal, fmt: 'money' },
      { label: 'GST', value: gstTotal, fmt: 'money' },
      { label: 'Grand Total', value: grand, fmt: 'money', emphasis: true, tone: 'net' },
    ],

    signatures: SIGNATURES.report,
  };
};

export default poReportConfig;